import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { Commands, Results } from '@local/client-contracts';
import { UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HighlightStyle } from '@shared/pipes';
import { hasDropdown, isOpenUrlCommand, windowSizeObserver } from '@shared/utils';
import { copyToClipboard } from '@shared/utils/command-util';
import { getIconModel } from '@shared/utils/set-icon.util';
import { isEqual } from 'lodash';
import { Tooltip } from 'primeng/tooltip';
import { DropdownComponent } from 'src/app/bar/components';
import { HubService } from 'src/app/bar/services/hub.service';

@UntilDestroy()
@Component({
  selector: 'result-bullet-part',
  templateUrl: './result-bullet-part.component.html',
  styleUrls: ['./result-bullet-part.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultBulletPartComponent implements OnInit, OnDestroy, AfterViewInit {
  private windowSize$ = windowSizeObserver();
  iconError = false;
  _model: Results.BulletPart;
  copyContent: string;
  highlightStyle: HighlightStyle = 'default';
  tooltipText: string = '';
  iconModel: UiIconModel;
  toggleCopyTooltip: WritableSignal<boolean> = signal(false);

  @Input() set model(v: Results.BulletPart) {
    if (isEqual(this._model, v)) {
      return;
    }
    this._model = v;
    this.onModelChange();
  }
  @Input() fallback: string;
  @Input() highlights: Array<string>;
  @Input() disableEnrichTooltip = false;
  @Output() invoke = new EventEmitter<Commands.Command>();

  get model() {
    return this._model;
  }

  get hasDropdown(): boolean {
    return hasDropdown(this.model);
  }

  get richTooltip() {
    return `${this.model?.text} - ${this.model?.tooltip}`;
  }

  private originalWidth: number;

  constructor(
    private viewContainerRef: ViewContainerRef,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private hubService: HubService
  ) {}

  ngAfterViewInit(): void {
    this.originalWidth = this.elementRef.nativeElement.offsetWidth;
    if (this.disableEnrichTooltip) {
      this.tooltipText = this.model?.tooltip;
      return;
    }
    this.windowSize$.pipe(untilDestroyed(this)).subscribe(() => {
      setTimeout(() => {
        const enrichTooltip = this.elementRef?.nativeElement?.offsetWidth < this.originalWidth;
        this.tooltipText = enrichTooltip ? this.richTooltip : this.model.tooltip;
        this.cdr.markForCheck();
      }, 0);
    });
  }

  ngOnInit(): void {
    this.onModelChange();
  }

  ngOnDestroy(): void {
    const elements = document.getElementsByClassName('u-tooltip');
    while (elements.length > 0) {
      elements[0].parentNode.removeChild(elements[0]);
    }
  }

  @ViewChild(Tooltip) tooltip;
  @ViewChild('dropdown') dropdown: DropdownComponent;
  @ViewChild('bullet', { read: ElementRef }) bullet: ElementRef;

  @HostBinding('class')
  get class(): Record<string, boolean> {
    return {
      clickable: this.model && (this.model.onClick != null || hasDropdown(this.model)),
      link: !!this.model.onClick?.type && isOpenUrlCommand(this.model.onClick),
    };
  }

  @HostListener('click')
  onClick(): void {
    if (this.hasDropdown) {
      this.openDropdown();
      return;
    }

    this.sendCommand(this.model.onClick);
  }

  @HostListener('contextmenu')
  onRightClick(): void {
    this.sendCommand(this.model.onRightClick);
  }

  private onModelChange() {
    this.copyContent = this.model?.onClick?.type === 'copy-clipboard' ? this.model?.text : undefined;
    this.tooltipText = this.copyContent ? 'Click to copy' : this.model.tooltip;
    const icon = this.model?.icon;
    this.iconModel = getIconModel(icon);
    this.cdr.markForCheck();
  }

  sendCommand(command: Commands.Command): void {
    if (!command) {
      return;
    }

    this.invoke.emit(command);
  }

  private openDropdown(): void {
    this.dropdown.open(this.elementRef, this.viewContainerRef);
  }

  async onCopyContent(): Promise<void> {
    if (!this.copyContent) return;
    await copyToClipboard(this.copyContent);
    this.toggleCopyTooltip.update((value) => !value);
  }
}
